import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { useEffect, useState } from 'react';
import filesManagement from '../../../network/filesManagement';
import { Spinner } from '../../../ui/Spinner';

type SummaryStatus = 'pending' | 'completed' | 'failed' | 'unsupported';

export default function DocumentSummaryModal({
  open,
  setOpen,
  file,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  file: any;
}) {
  const [summary, setSummary] = useState<string | null>(null);
  const [status, setStatus] = useState<SummaryStatus>('pending');
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const fetchSummary = async () => {
    if (!file?.id) {
      return;
    }
    setIsLoading(true);
    try {
      const data = await filesManagement.getDocumentSummary(file.id);
      setSummary(data?.summary ?? null);
      setStatus((data?.status as SummaryStatus) ?? 'pending');
      setError(data?.error ?? null);
      setUpdatedAt(data?.updated_at ?? null);
    } catch (fetchError) {
      setStatus('failed');
      setError('Unable to load document summary.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!file?.id) {
      return;
    }
    setIsRegenerating(true);
    setStatus('pending');
    setError(null);
    try {
      await filesManagement.regenerateDocumentSummary(file.id);
      await fetchSummary();
    } catch (requestError) {
      setStatus('failed');
      setError('Failed to start summary generation.');
    } finally {
      setIsRegenerating(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (open && file?.id) {
      fetchSummary();
    } else {
      setSummary(null);
      setStatus('pending');
      setError(null);
      setUpdatedAt(null);
    }
  }, [open, file?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const statusLabel = {
    pending: 'Summary generation in progress.',
    completed: 'Summary ready.',
    failed: 'Summary generation failed.',
    unsupported: 'Summary not available for this file type.',
  }[status];

  const formattedUpdatedAt = updatedAt
    ? new Date(updatedAt).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center">
          <DialogPanel
            transition
            className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          >
            <div className="bg-white px-6 pt-5">
              <div className="flex flex-col">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold text-gray-900"
                >
                  Document summary
                </DialogTitle>
                <div className="mb-4 mt-2">
                  <p className="text-sm text-gray-500">
                    {file?.name ? `Summary for "${file.name}".` : 'Summary'}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 pb-4">
              {isLoading ? (
                <div className="flex flex-col items-center gap-2 py-6">
                  <Spinner size={8} />
                  <p className="text-sm text-gray-500">Loading summary...</p>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                    <p className="font-medium text-gray-700">{statusLabel}</p>
                    {formattedUpdatedAt ? (
                      <p className="text-xs text-gray-500">
                        Last updated: {formattedUpdatedAt}
                      </p>
                    ) : null}
                    {error ? (
                      <p className="mt-1 text-xs text-red-600">{error}</p>
                    ) : null}
                  </div>
                  {status === 'completed' ? (
                    <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white p-3 text-sm text-gray-700">
                      {summary ? (
                        <p className="whitespace-pre-wrap">{summary}</p>
                      ) : (
                        <p className="text-gray-500">No summary available.</p>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>

            <div className="flex flex-row justify-end gap-3 px-6 pb-5">
              <button
                type="button"
                data-autofocus
                onClick={() => setOpen(false)}
                className="inline-flex justify-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={fetchSummary}
                disabled={isLoading}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={isRegenerating || status === 'pending'}
                className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isRegenerating ? 'Starting...' : 'Regenerate summary'}
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
